'use client'

/**
 * Thin DOM wrapper: rasterize the current page to a JPEG blob for a support
 * ticket. Uses html-to-image (SVG foreignObject — no browser permission
 * prompt, works inside the PWA), dynamically imported so the library stays out
 * of the main bundle. Anything marked `data-screenshot-exclude` (the feedback
 * modals, the error banner) is left out so the shot shows the page behind
 * them. Best-effort by design: every failure path returns null and the caller
 * simply submits the ticket without a screenshot.
 */

import { runWithTimeout } from '@/shared/lib/offline/withTimeout'
import { screenshotJpegScale } from './errorReport'

const CAPTURE_TIMEOUT_MS = 20000
const MAX_EDGE_PX = 1600
const JPEG_QUALITY = 0.8

export const SCREENSHOT_EXCLUDE_ATTR = 'data-screenshot-exclude'

export async function capturePageScreenshot(): Promise<Blob | null> {
  if (typeof document === 'undefined') return null
  try {
    const { toCanvas } = await import('html-to-image')
    const canvas = await runWithTimeout(
      () =>
        toCanvas(document.body, {
          backgroundColor: '#ffffff',
          pixelRatio: 1,
          // Embedding web fonts is the slow path (it re-fetches and inlines
          // every font face); the fallback font is fine for a bug screenshot.
          skipFonts: true,
          filter: (node) =>
            !(node instanceof Element && node.hasAttribute(SCREENSHOT_EXCLUDE_ATTR)),
        }),
      CAPTURE_TIMEOUT_MS
    )
    return await canvasToJpeg(canvas)
  } catch (err) {
    console.warn('[errorReport] screenshot capture failed:', err)
    return null
  }
}

async function canvasToJpeg(source: HTMLCanvasElement): Promise<Blob | null> {
  const scale = screenshotJpegScale(source.width, source.height, MAX_EDGE_PX)
  let canvas = source
  if (scale < 1) {
    const scaled = document.createElement('canvas')
    scaled.width = Math.round(source.width * scale)
    scaled.height = Math.round(source.height * scale)
    const ctx = scaled.getContext('2d')
    if (!ctx) return null
    ctx.drawImage(source, 0, 0, scaled.width, scaled.height)
    canvas = scaled
  }
  return new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, 'image/jpeg', JPEG_QUALITY)
  )
}
